<template>
  <section id="transactions-list">
    <!-- Mobile Header -->
    <div class="custom-mobile-header d-md-none">
      <div class="header-content">
        <button type="button" class="back-button" @click="goBack">
          <v-icon size="25" color="#fff">mdi-arrow-right-bold</v-icon>
        </button>
        <div class="header-title">تراکنش‌های من</div>
      </div>
    </div>

    <b-card class="main-card">
      <!-- Page Header -->
      <div class="page-header mb-4">
        <div class="d-flex justify-content-between align-items-center flex-wrap">
          <div class="d-flex align-items-center">
            <div class="header-icon ml-3">
              <v-icon size="24" color="white">mdi-receipt-text</v-icon>
            </div>
            <div>
              <h4 class="mb-1 text-white">تراکنش‌های من</h4>
              <p class="mb-0 text-white-50">تاریخچه پرداخت‌های شما</p>
            </div>
          </div>
          <b-button variant="light" size="sm" class="mt-2 mt-md-0" @click="$router.push({ name: 'subscription-renew' })">
            <v-icon size="18" class="ml-1">mdi-plus</v-icon>
            تمدید اشتراک
          </b-button>
        </div>
      </div>

      <!-- Stats Cards -->
      <div class="stats-grid mb-4">
        <div class="stat-card success">
          <div class="stat-icon">
            <v-icon color="white">mdi-check-circle</v-icon>
          </div>
          <div class="stat-value">{{ stats.success.count }}</div>
          <div class="stat-label">تراکنش موفق</div>
        </div>
        <div class="stat-card danger">
          <div class="stat-icon">
            <v-icon color="white">mdi-close-circle</v-icon>
          </div>
          <div class="stat-value">{{ stats.failed.count }}</div>
          <div class="stat-label">تراکنش ناموفق</div>
        </div>
        <div class="stat-card warning">
          <div class="stat-icon">
            <v-icon color="white">mdi-clock-outline</v-icon>
          </div>
          <div class="stat-value">{{ stats.pending.count }}</div>
          <div class="stat-label">در انتظار</div>
        </div>
        <div class="stat-card primary">
          <div class="stat-icon">
            <v-icon color="white">mdi-cash-multiple</v-icon>
          </div>
          <div class="stat-value">{{ formatPrice(stats.success.amount / 10) }}</div>
          <div class="stat-label">مجموع پرداختی (تومان)</div>
        </div>
      </div>

      <!-- Filters -->
      <div class="filters-card mb-4">
        <div class="filter-row">
          <div class="search-box">
            <v-icon size="20" class="search-icon">mdi-magnify</v-icon>
            <input
              v-model="search"
              type="text"
              placeholder="جستجو..."
              @input="onSearch"
            >
          </div>
          <div class="filter-tabs">
            <button
              v-for="tab in filterTabs"
              :key="tab.value"
              :class="['filter-tab', { active: activeFilter === tab.value }]"
              @click="setFilter(tab.value)"
            >
              {{ tab.label }}
            </button>
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-5">
        <b-spinner variant="primary" />
        <p class="mt-2">در حال بارگذاری...</p>
      </div>

      <!-- Transactions List -->
      <div v-else-if="transactions.length > 0" class="transactions-grid">
        <div
          v-for="tx in transactions"
          :key="tx._id"
          class="transaction-card"
          @click="showDetail(tx)"
        >
          <div class="tx-header">
            <span class="tx-order-id">#{{ tx.orderId }}</span>
            <b-badge :variant="getStatusVariant(tx.status)" pill>
              {{ getStatusText(tx.status) }}
            </b-badge>
          </div>
          <div class="tx-body">
            <div class="tx-device">
              <v-icon size="18" class="ml-2">mdi-cellphone-link</v-icon>
              {{ getDeviceTitle(tx) }}
            </div>
            <div class="tx-plan">
              <v-icon size="18" class="ml-2">mdi-tag</v-icon>
              {{ tx.planId ? tx.planId.title : '-' }} ({{ tx.planId ? tx.planId.period : '-' }} ماهه)
            </div>
          </div>
          <div class="tx-footer">
            <div class="tx-amount">
              {{ formatPrice(tx.amount / 10) }} تومان
            </div>
            <div class="tx-date">
              {{ formatDate(tx.creationTime) }}
            </div>
          </div>
          <div v-if="tx.refNumber" class="tx-ref">
            شماره مرجع: {{ tx.refNumber }}
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="empty-state text-center py-5">
        <v-icon size="64" color="grey">mdi-receipt-text-outline</v-icon>
        <h5 class="mt-3">تراکنشی یافت نشد</h5>
        <p class="text-muted">هنوز تراکنشی ثبت نشده است</p>
        <b-button variant="primary" @click="$router.push({ name: 'subscription-renew' })">
          تمدید اشتراک
        </b-button>
      </div>

      <!-- Pagination -->
      <div v-if="pagination.pages > 1" class="pagination-wrapper">
        <span class="pagination-info">
          نمایش {{ (pagination.page - 1) * pagination.limit + 1 }} تا {{ Math.min(pagination.page * pagination.limit, pagination.total) }} از {{ pagination.total }}
        </span>
        <div class="pagination-btns">
          <button
            class="page-btn"
            :disabled="pagination.page === 1"
            @click="goToPage(pagination.page - 1)"
          >
            <v-icon size="16">mdi-chevron-right</v-icon>
          </button>
          <button
            v-for="p in visiblePages"
            :key="p"
            :class="['page-btn', { active: p === pagination.page }]"
            @click="goToPage(p)"
          >
            {{ p }}
          </button>
          <button
            class="page-btn"
            :disabled="pagination.page === pagination.pages"
            @click="goToPage(pagination.page + 1)"
          >
            <v-icon size="16">mdi-chevron-left</v-icon>
          </button>
        </div>
      </div>
    </b-card>

    <!-- Transaction Detail Modal -->
    <b-modal
      ref="detail-modal"
      title="جزئیات تراکنش"
      centered
      hide-footer
    >
      <div v-if="selectedTransaction" class="detail-content">
        <div class="detail-row">
          <span class="detail-label">شماره سفارش:</span>
          <span class="detail-value">#{{ selectedTransaction.orderId }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">وضعیت:</span>
          <b-badge :variant="getStatusVariant(selectedTransaction.status)" pill>
            {{ getStatusText(selectedTransaction.status) }}
          </b-badge>
        </div>
        <div class="detail-row">
          <span class="detail-label">دستگاه:</span>
          <span class="detail-value">{{ getDeviceTitle(selectedTransaction) }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">پلن:</span>
          <span class="detail-value">{{ selectedTransaction.planId ? selectedTransaction.planId.title : '-' }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">مبلغ:</span>
          <span class="detail-value text-primary font-weight-bold">{{ formatPrice(selectedTransaction.amount / 10) }} تومان</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">تاریخ ایجاد:</span>
          <span class="detail-value">{{ formatDateTime(selectedTransaction.creationTime) }}</span>
        </div>
        <div v-if="selectedTransaction.paymentTime" class="detail-row">
          <span class="detail-label">تاریخ پرداخت:</span>
          <span class="detail-value">{{ formatDateTime(selectedTransaction.paymentTime) }}</span>
        </div>
        <div v-if="selectedTransaction.refNumber" class="detail-row">
          <span class="detail-label">شماره مرجع:</span>
          <span class="detail-value font-weight-bold">{{ selectedTransaction.refNumber }}</span>
        </div>
        <div v-if="selectedTransaction.trackingCode" class="detail-row">
          <span class="detail-label">کد پیگیری:</span>
          <span class="detail-value">{{ selectedTransaction.trackingCode }}</span>
        </div>
        <div v-if="selectedTransaction.errorMessage" class="detail-row">
          <span class="detail-label">پیام خطا:</span>
          <span class="detail-value text-danger">{{ selectedTransaction.errorMessage }}</span>
        </div>
      </div>
    </b-modal>

    <!-- Mobile Footer -->
    <footer class="mobile-footer d-md-none">
      <v-list dense>
        <v-list-item-group v-model="selectedFooterItem" color="primary">
          <v-list-item
            v-for="item in footerItems"
            :key="item.keyItem"
            @click="$router.push({ name: item.rout })"
          >
            <v-list-item-icon>
              <v-icon v-text="item.icon" />
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title v-text="item.text" />
            </v-list-item-content>
          </v-list-item>
        </v-list-item-group>
      </v-list>
    </footer>
  </section>
</template>

<script>
import {
  BCard, BButton, BBadge, BSpinner, BModal,
} from 'bootstrap-vue'
import PaymentService from '@/services/payment.service'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BCard,
    BButton,
    BBadge,
    BSpinner,
    BModal,
  },
  data() {
    return {
      loading: true,
      transactions: [],
      search: '',
      activeFilter: 'all',
      pagination: {
        page: 1,
        limit: 10,
        total: 0,
        pages: 0,
      },
      stats: {
        total: 0,
        success: { count: 0, amount: 0 },
        failed: { count: 0, amount: 0 },
        pending: { count: 0, amount: 0 },
        cancelled: { count: 0, amount: 0 },
      },
      selectedTransaction: null,
      filterTabs: [
        { label: 'همه', value: 'all' },
        { label: 'موفق', value: 'success' },
        { label: 'ناموفق', value: 'failed' },
        { label: 'در انتظار', value: 'pending' },
      ],
      selectedFooterItem: 0,
      footerItems: [
        { keyItem: 0, text: 'خانه', icon: 'mdi-home', rout: 'home' },
        { keyItem: 1, text: 'دستگاه‌ها', icon: 'mdi-cellphone-link', rout: 'device-list' },
        { keyItem: 2, text: 'پروفایل', icon: 'mdi-account', rout: 'profile' },
      ],
      searchTimeout: null,
    }
  },
  computed: {
    visiblePages() {
      const pages = []
      const current = this.pagination.page
      const total = this.pagination.pages

      let start = Math.max(1, current - 2)
      let end = Math.min(total, current + 2)

      if (end - start < 4) {
        if (start === 1) {
          end = Math.min(total, 5)
        } else {
          start = Math.max(1, total - 4)
        }
      }

      for (let i = start; i <= end; i++) {
        pages.push(i)
      }

      return pages
    },
  },
  async mounted() {
    await this.loadStats()
    await this.loadTransactions()
  },
  methods: {
    async loadStats() {
      try {
        const res = await PaymentService.getStats()
        if (res.status === 'success') {
          this.stats = res.result
        }
      } catch (error) {
        console.error('Error loading stats:', error)
      }
    },

    async loadTransactions() {
      try {
        this.loading = true

        const params = {
          page: this.pagination.page,
          limit: this.pagination.limit,
        }

        if (this.activeFilter !== 'all') {
          params.status = this.activeFilter
        }

        const res = await PaymentService.getList(params)

        if (res.status === 'success') {
          this.transactions = res.result.payments || []
          this.pagination = res.result.pagination || this.pagination
        }
      } catch (error) {
        console.error('Error loading transactions:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'خطا',
            text: 'خطا در بارگذاری تراکنش‌ها',
            icon: 'XIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.loading = false
      }
    },

    setFilter(filter) {
      this.activeFilter = filter
      this.pagination.page = 1
      this.loadTransactions()
    },

    onSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.pagination.page = 1
        this.loadTransactions()
      }, 500)
    },

    goToPage(page) {
      if (page >= 1 && page <= this.pagination.pages) {
        this.pagination.page = page
        this.loadTransactions()
      }
    },

    showDetail(tx) {
      this.selectedTransaction = tx
      this.$refs['detail-modal'].show()
    },

    getDeviceTitle(tx) {
      if (!tx || !tx.deviceId) return '-'
      return tx.deviceId.title || tx.deviceId.imei || '-'
    },

    getStatusVariant(status) {
      const variants = {
        success: 'success',
        failed: 'danger',
        pending: 'warning',
        cancelled: 'secondary',
      }
      return variants[status] || 'secondary'
    },

    getStatusText(status) {
      const texts = {
        success: 'موفق',
        failed: 'ناموفق',
        pending: 'در انتظار',
        cancelled: 'انصراف',
      }
      return texts[status] || status
    },

    formatPrice(amount) {
      return new Intl.NumberFormat('fa-IR').format(amount)
    },

    formatDate(timestamp) {
      if (!timestamp) return '-'
      return new Date(timestamp * 1000).toLocaleDateString('fa-IR')
    },

    formatDateTime(timestamp) {
      if (!timestamp) return '-'
      const date = new Date(timestamp * 1000)
      return `${date.toLocaleDateString('fa-IR')} - ${date.toLocaleTimeString('fa-IR')}`
    },

    goBack() {
      this.$router.go(-1)
    },
  },
}
</script>

<style scoped>
#transactions-list {
  padding-bottom: 80px;
}

.main-card {
  background: #f8fafc !important;
  border: none !important;
  min-height: 100vh;
}

.page-header {
  padding: 24px;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  border-radius: 16px;
}

.header-icon {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.stat-card {
  background: white;
  border-radius: 16px;
  padding: 20px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
}

.stat-card::before {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 80px;
  height: 80px;
  border-radius: 0 0 0 80px;
  opacity: 0.1;
}

.stat-card.success::before { background: #22c55e; }
.stat-card.danger::before { background: #ef4444; }
.stat-card.warning::before { background: #f59e0b; }
.stat-card.primary::before { background: #0ea5e9; }

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
}

.stat-card.success .stat-icon { background: linear-gradient(135deg, #22c55e, #16a34a); }
.stat-card.danger .stat-icon { background: linear-gradient(135deg, #ef4444, #dc2626); }
.stat-card.warning .stat-icon { background: linear-gradient(135deg, #f59e0b, #d97706); }
.stat-card.primary .stat-icon { background: linear-gradient(135deg, #0ea5e9, #0284c7); }

.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
}

.stat-label {
  font-size: 13px;
  color: #64748b;
}

/* Filters */
.filters-card {
  background: white;
  border-radius: 16px;
  padding: 16px 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
}

.filter-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.search-box {
  position: relative;
  flex: 1;
  min-width: 200px;
}

.search-box input {
  width: 100%;
  padding: 12px 16px 12px 44px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 14px;
  background: #f8fafc;
  transition: all 0.2s ease;
}

.search-box input:focus {
  outline: none;
  border-color: #0ea5e9;
  background: white;
}

.search-box .search-icon {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
}

.filter-tabs {
  display: flex;
  gap: 8px;
  background: #f1f5f9;
  padding: 4px;
  border-radius: 12px;
}

.filter-tab {
  padding: 10px 18px;
  border: none;
  background: transparent;
  border-radius: 10px;
  font-weight: 500;
  font-size: 13px;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;
}

.filter-tab:hover {
  color: #0ea5e9;
}

.filter-tab.active {
  background: white;
  color: #0ea5e9;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

/* Transactions Grid */
.transactions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
}

.transaction-card {
  background: white;
  border-radius: 16px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
}

.transaction-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
}

.tx-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.tx-order-id {
  font-weight: 700;
  color: #0ea5e9;
}

.tx-body {
  margin-bottom: 16px;
}

.tx-device,
.tx-plan {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #475569;
  margin-bottom: 8px;
}

.tx-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  border-top: 1px solid #f1f5f9;
}

.tx-amount {
  font-size: 16px;
  font-weight: 700;
  color: #22c55e;
}

.tx-date {
  font-size: 12px;
  color: #94a3b8;
}

.tx-ref {
  font-size: 11px;
  color: #0ea5e9;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed #e2e8f0;
}

/* Pagination */
.pagination-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  padding: 16px 20px;
  background: white;
  border-radius: 16px;
}

.pagination-info {
  font-size: 13px;
  color: #64748b;
}

.pagination-btns {
  display: flex;
  gap: 8px;
}

.page-btn {
  min-width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 2px solid #e2e8f0;
  background: white;
  color: #64748b;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.page-btn:hover:not(:disabled) {
  border-color: #0ea5e9;
  color: #0ea5e9;
}

.page-btn.active {
  background: #0ea5e9;
  border-color: #0ea5e9;
  color: white;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Detail Modal */
.detail-content {
  padding: 8px 0;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f1f5f9;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  font-size: 13px;
  color: #64748b;
}

.detail-value {
  font-size: 14px;
  color: #1e293b;
}

/* Mobile Header */
.custom-mobile-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  z-index: 999;
  display: flex;
  align-items: center;
  padding: 0 15px;
}

.custom-mobile-header .header-content {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.custom-mobile-header .back-button {
  position: absolute;
  right: 0;
  background: none;
  border: none;
  padding: 5px;
}

.custom-mobile-header .header-title {
  color: white;
  font-size: 18px;
  font-weight: 600;
}

/* Mobile Footer */
.mobile-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top: 1px solid #e2e8f0;
  z-index: 999;
}

/* Responsive */
@media (max-width: 991px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  #transactions-list {
    margin: -28px;
    padding-top: 60px;
  }

  .main-card {
    border-radius: 0 !important;
  }

  .page-header {
    border-radius: 0;
    margin: -20px -20px 20px -20px;
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  .stat-card {
    padding: 16px;
  }

  .stat-value {
    font-size: 20px;
  }

  .filter-row {
    flex-direction: column;
    align-items: stretch;
  }

  .search-box {
    width: 100%;
  }

  .filter-tabs {
    width: 100%;
    overflow-x: auto;
    justify-content: flex-start;
  }

  .filter-tab {
    white-space: nowrap;
  }

  .transactions-grid {
    grid-template-columns: 1fr;
  }

  .pagination-wrapper {
    flex-direction: column;
    gap: 16px;
  }
}
</style>
